import { computeAlignment, computeCanonicalRender } from './photoAligner';

// A manual fine-tune transform, in normalized units (translate = fraction of
// frame, scale = multiplier, rotate = degrees). Applied on top of each photo's
// canonical (panel-fill) framing.
export const IDENTITY = { scale: 1, translateX: 0, translateY: 0, rotate: 0 };

export const SCALE_MIN = 0.25;
export const SCALE_MAX = 3;
// Fine-grained on purpose: one tap nudges, and press-and-hold repeats for a
// big move (see useHoldRepeat in PhotoComparisonViewer).
const ZOOM_STEP = 0.03;
const MAX_TRANSLATE = 0.4; // keep the body from being pushed fully off-panel

export function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

export function isIdentity(m) {
  if (!m) return true;
  return m.scale === 1 && m.translateX === 0 && m.translateY === 0 && m.rotate === 0;
}

// Rotate a vector by `deg` degrees (CSS rotate direction: y grows downward).
function rotateVec(x, y, deg) {
  const r = (deg || 0) * Math.PI / 180;
  const c = Math.cos(r), s = Math.sin(r);
  return { x: c * x - s * y, y: s * x + c * y };
}

// direction: +1 zoom in, -1 zoom out.
//
// Pass `canonical` to zoom AROUND THE HEAD instead of around the body's
// bounding-box center. The rendered transform pivots on the bbox center, so a
// plain scale change slides the head up or down the panel and undoes the head
// alignment the coach just set with Auto. Compensating the translate keeps the
// head pinned while the body grows/shrinks around it.
export function zoom(manual, direction, canonical = null) {
  const m = manual || IDENTITY;
  const scale = clamp(m.scale + direction * ZOOM_STEP, SCALE_MIN, SCALE_MAX);
  if (!canonical || scale === m.scale) return { ...m, scale };

  // Head stays put when  t + R·s·v  is invariant  ⇒  t₁ = t₀ + R·((s₀ − s₁)·v)
  const r = computeCanonicalRender(canonical);
  const d = rotateVec(
    (m.scale - scale) * r.headOffsetXPct,
    (m.scale - scale) * r.headOffsetYPct,
    m.rotate,
  );
  return {
    ...m,
    scale,
    translateX: clamp(m.translateX + d.x / 100, -MAX_TRANSLATE, MAX_TRANSLATE),
    translateY: clamp(m.translateY + d.y / 100, -MAX_TRANSLATE, MAX_TRANSLATE),
  };
}

// Apply a drag delta in CSS pixels, converting to normalized frame units using
// the rendered panel size. Accumulates on the existing translate, clamped.
export function panBy(manual, dxPx, dyPx, panelW, panelH) {
  const m = manual || IDENTITY;
  const tx = clamp(m.translateX + dxPx / Math.max(panelW, 1), -MAX_TRANSLATE, MAX_TRANSLATE);
  const ty = clamp(m.translateY + dyPx / Math.max(panelH, 1), -MAX_TRANSLATE, MAX_TRANSLATE);
  return { ...m, translateX: tx, translateY: ty };
}

// Snap `from` to match `to`: match body size, tilt, AND put the top of the
// head on exactly the same panel line as the other photo.
//
// Each photo is pre-normalized to fill its panel by its canonical bounding box
// (render scales by 1/max(width,height)). computeAlignment.scale is the raw
// image-space shoulder-width ratio, so the true on-panel residual is
// align.scale * (canonFrom / canonTo), where canon = max(width, height).
//
// Head lock: translate used to be 0 on the theory that per-photo canonical
// head-anchoring had already placed both heads. It hadn't — the residual scale
// pivots on the bounding-box CENTRE, which drags the head off the anchor by
// (1 − scale) × its distance from that centre, and the anchoring shift itself
// is capped at MAX_HEAD_SHIFT_PCT so the two photos can start out disagreeing.
// So solve for the translate directly instead. With the composed transform
//   head' = finalHead + Sc·(t + (R·Sm − I)·v)
// setting head'_from = finalHead_to gives
//   t = (finalHead_to − finalHead_from)/Sc + (v − R·Sm·v)
// where v is the head's offset from the transform origin and Sc the canonical
// fill scale — all in percent of the panel box.
export function autoMatch(fromKp, toKp, poseType, fromCanonical, toCanonical, toPoseType = poseType) {
  if (!fromKp || !toKp) return null;
  const a = computeAlignment(fromKp, toKp, poseType, toPoseType);
  if (!a) return null;
  const canonFrom = Math.max(fromCanonical?.width ?? 1, fromCanonical?.height ?? 1);
  const canonTo = Math.max(toCanonical?.width ?? 1, toCanonical?.height ?? 1);
  const residualScale = clamp(a.scale * (canonFrom / canonTo), SCALE_MIN, SCALE_MAX);

  const from = computeCanonicalRender(fromCanonical);
  const to = computeCanonicalRender(toCanonical);
  const scaled = rotateVec(
    residualScale * from.headOffsetXPct,
    residualScale * from.headOffsetYPct,
    a.rotate,
  );
  const txPct = (to.finalHeadXPct - from.finalHeadXPct) / from.scale
    + (from.headOffsetXPct - scaled.x);
  const tyPct = (to.finalHeadYPct - from.finalHeadYPct) / from.scale
    + (from.headOffsetYPct - scaled.y);

  return {
    scale: residualScale,
    translateX: clamp(txPct / 100, -MAX_TRANSLATE, MAX_TRANSLATE),
    translateY: clamp(tyPct / 100, -MAX_TRANSLATE, MAX_TRANSLATE),
    rotate: a.rotate,
  };
}
